import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

class ShippingProtection extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="checkbox"]');
    this.status = this.querySelector('[data-shipping-protection-status]');

    if (!this.input) return;

    this.input.addEventListener('change', this.handleChange);

    if (Number(this.dataset.merchandiseCount) === 0 && this.input.checked) {
      queueMicrotask(() => this.updateProtection(false));
    }
  }

  disconnectedCallback() {
    this.input?.removeEventListener('change', this.handleChange);
  }

  handleChange = () => {
    this.updateProtection(Boolean(this.input?.checked));
  };

  async updateProtection(shouldAdd) {
    if (!this.input || this.input.disabled) return;

    const variantId = Number(this.dataset.variantId);
    const lineKey = this.dataset.lineKey;
    const cartComponents = [...document.querySelectorAll('cart-items-component[data-section-id]')];
    const sectionIds = [...new Set(cartComponents.map((component) => component.dataset.sectionId).filter(Boolean))];
    const deferred = CartLinesUpdateEvent.createPromise();

    this.input.disabled = true;
    this.setAttribute('aria-busy', 'true');
    this.status?.classList.remove('shipping-protection__status--error');
    if (this.status) this.status.textContent = this.dataset.updatingText || '';

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: shouldAdd ? 'add' : 'remove',
        context: 'cart',
        lines: shouldAdd
          ? [{ merchandiseId: String(variantId), quantity: 1 }]
          : [{ id: lineKey || '', quantity: 0 }],
        promise: deferred.promise,
      })
    );

    try {
      const root = window.Shopify?.routes?.root || '/';
      const endpoint = shouldAdd ? `${root}cart/add.js` : `${root}cart/change.js`;
      const payload = shouldAdd
        ? {
            items: [{ id: variantId, quantity: 1, properties: { _shipping_protection: 'true' } }],
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }
        : {
            id: lineKey,
            quantity: 0,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          };
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!response.ok || result.errors || result.status) {
        throw new Error(result.description || result.message || 'Unable to update shipping protection');
      }

      const firstSection = sectionIds.find((id) => result.sections?.[id]);
      const sectionHtml = firstSection ? result.sections[firstSection] : '';
      const parsedSection = sectionHtml ? new DOMParser().parseFromString(sectionHtml, 'text/html') : null;
      const itemCount = Number(parsedSection?.querySelector('[ref="cartItemCount"]')?.textContent || 0);

      deferred.resolve({
        detail: {
          sections: result.sections || {},
          items: result.items || [],
          itemCount,
          source: 'shipping-protection',
          didError: false,
        },
      });
    } catch (error) {
      this.input.checked = !shouldAdd;
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Unable to update shipping protection',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      if (this.status) {
        this.status.textContent = this.dataset.errorText || '';
        this.status.classList.add('shipping-protection__status--error');
      }
    } finally {
      this.input.disabled = false;
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('shipping-protection')) {
  customElements.define('shipping-protection', ShippingProtection);
}
